import {
  BanknoteArrowUp,
  BookCheck,
  CheckIcon,
  ClipboardCheck,
  Clock,
  Coins,
  DollarSign,
  Gift,
  HandCoins,
  Handshake,
  Receipt,
  RefreshCwOff,
} from "lucide-react";

import {
  ContractTypeValuesFor,
  ProductTypes,
} from "../../constants/purchaseFlow";
import { usePrePurchaseURLParams } from "../../hooks/usePrePurchaseURLParams";
import { cn } from "../../utils/styling";
import { BenefitItem } from "./ProductOption";

export function ContractTypeOption({
  product,
  variant,
  contractTypeUrlParam,
}: {
  product: ProductTypes;
  variant: ContractTypeValuesFor<ProductTypes>;
  contractTypeUrlParam: ContractTypeValuesFor<ProductTypes>;
}) {
  const { setURLParam } = usePrePurchaseURLParams();

  const handleContractTypeOptionPress = () => {
    if (contractTypeUrlParam === variant) {
      setURLParam("contractType", null);
    } else {
      // Select new product and reset contractType
      setURLParam(
        "contractType",
        variant as ContractTypeValuesFor<ProductTypes>,
      );
    }
  };

  return (
    <button
      onClick={handleContractTypeOptionPress}
      className={cn(
        "text-white min-h-[100px] w-full rounded-2xl flex flex-col overflow-hidden box-border border-1 relative",
        product === "nettle" && variant === "onceOff" && "bg-surface-two",
        product === "nettle" && variant === "monthly" && "bg-brand-primary",
        product === "membershipOnly" &&
          variant === "monthly" &&
          "bg-surface-two",
        product === "membershipOnly" &&
          variant === "annual" &&
          "bg-brand-primary",
        variant === contractTypeUrlParam
          ? "border-brand-primary-darker shadow-lg shadow-brand-primary-darker/20"
          : "border-border",
      )}
    >
      <HeaderRow
        text={
          product === "nettle"
            ? variant === "onceOff"
              ? "Best Value"
              : "Most Flexible"
            : product === "membershipOnly"
              ? variant === "annual"
                ? "Best Value"
                : "Most Flexible"
              : "DEBUG"
        }
        backgroundColor={
          product === "nettle" && variant === "onceOff"
            ? "beige"
            : product === "nettle" && variant === "monthly"
              ? "blue"
              : product === "membershipOnly" && variant === "monthly"
                ? "beige"
                : "blue"
        }
        isActive={contractTypeUrlParam === variant}
      />
      <div className="w-full text-foreground-primary bg-canvas-base text-lg grow rounded-tl-2xl rounded-tr-2xl border-border border-t-1 relative overflow-hidden">
        <div className="w-full flex flex-row items-center justify-between px-4 pt-2 pb-2">
          <p className="text-xl font-bold text-left text-foreground-primary mb-0">
            {product === "nettle"
              ? variant === "onceOff"
                ? "Buy Nettle™"
                : "Rent to Own Nettle™"
              : product === "membershipOnly"
                ? variant === "annual"
                  ? "Annual Subscription"
                  : "Monthly Subscription"
                : ""}
          </p>
          <p className="text-sm font-bold text-right mb-0">
            {product === "nettle"
              ? variant === "onceOff"
                ? "449 GBP once"
                : "59.99 GBP / monthly"
              : product === "membershipOnly"
                ? variant === "annual"
                  ? "24.99 GBP / year"
                  : "2.99 GBP / month"
                : "DEBUG"}
          </p>
        </div>
        <div className="mx-2 h-[1px] bg-border"></div>
        <div className="w-full px-4 pt-2 pb-0 text-sm  justify-start text-left flex flex-col text-foreground-secondary">
          {product === "nettle" && variant === "monthly" && (
            <>
              <BenefitItem
                benefit="3 month minimum, return Nettle™ to cancel"
                icon={Clock}
              />
              <BenefitItem
                benefit="After 12 months, own Nettle™ for life"
                icon={Gift}
              />
              <BenefitItem
                benefit="Ongoing free Samphire membership and Nettle™ refills"
                icon={BookCheck}
              />
            </>
          )}
          {product === "nettle" && variant === "onceOff" && (
            <>
              <BenefitItem
                benefit="One off payment, own Nettle™ forever"
                icon={Handshake}
              />
              <BenefitItem
                benefit="90-day money back guarantee"
                icon={Receipt}
              />
              <BenefitItem
                benefit="1 year free Samphire membership and Nettle™ refills"
                icon={BookCheck}
              />
            </>
          )}
          {product === "membershipOnly" && variant === "monthly" && (
            <>
              <BenefitItem benefit="Pay as you go" icon={Coins} />
              <BenefitItem benefit="Cancel anytime" icon={RefreshCwOff} />
              <BenefitItem
                benefit="Most flexible option"
                icon={BanknoteArrowUp}
              />
            </>
          )}
          {product === "membershipOnly" && variant === "annual" && (
            <>
              <BenefitItem
                benefit="Ideal for long term support"
                icon={ClipboardCheck}
              />
              <BenefitItem
                benefit="Save 30% off the monthly price"
                icon={DollarSign}
              />
              <BenefitItem benefit="Best value" icon={HandCoins} />
            </>
          )}
        </div>
        {product === "nettle" && (
          <div className="px-4 pb-4 text-sm text-brand-primary-darker font-bold w-full text-left flex flex-row items-center gap-1">
            {/* <TruckIcon className="w-4 h-4" /> */}
            <p>Ships to EU and UK</p>
          </div>
        )}
        {variant !== contractTypeUrlParam && (
          <div className="w-full h-full absolute top-0 left-0 bg-white/50"></div>
        )}
      </div>
    </button>
  );
}

function HeaderRow({
  text,
  isActive,
  backgroundColor,
}: {
  text: string;
  isActive: boolean;
  backgroundColor: "blue" | "beige";
}) {
  return (
    <div className="w-full py-2 flex flex-row items-center justify-center  text-center relative">
      <p
        className={cn(
          "text-sm font-medium",
          backgroundColor === "blue" && "text-white",
          backgroundColor === "beige" && "text-foreground-primary",
        )}
      >
        {text}
      </p>
      <div className="absolute right-2 w-6 h-6 bg-white rounded-full border-1 border-border flex items-center justify-center">
        {isActive && (
          <CheckIcon className={cn("w-4 h-4  stroke-3 text-brand-primary")} />
        )}
      </div>
    </div>
  );
}
